import PropTypes from 'prop-types';
import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import {LabAutomationFeature, RegistryFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {areAnyFeaturesEnabled, isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as DataTableUtil from 'notebook/data-tables/DataTableUtil';
import {isDataTableFeatureEnabled} from 'notebook/data-tables/DataTableUtil';
import {allowPlateMaps} from 'notebook/integrations/plate-maps/FixedPlateEditorUtil';
import {
  ParameterStructuredTableMode,
  ParameterStructuredTableModeToIcon,
} from 'notebook/integrations/procedures/ParameterStructuredTableConstants';
import FormatState from 'notebook/mediocre/formatted-text/FormatState';
import FormattersConstants, {FormatData} from 'notebook/mediocre/formatted-text/FormattersConstants';
import StaticFormatToolbar, {
  ToolbarFormatOption,
  ToolbarFormatOptionWithDropdown,
} from 'notebook/mediocre/toolbars/StaticFormatToolbar';
import {ListType} from 'notebook/notes/lists/ListConstants';
import NoteConstants, {PRINT_OPTION} from 'notebook/notes/NoteConstants';
import InsertTableComponent from 'notebook/plain-tables/InsertTableComponent';
import InsertWellPlateMenu from 'notebook/plain-tables/InsertWellPlateMenu';
import * as RegistrationTableUtil from 'notebook/registration-tables/RegistrationTableUtil';
import {StructuredTable, StructuredTableKind} from 'notebook/structured-tables/StructuredTableConstants';
import isStructuredTableSupportedForFile from 'notebook/structured-tables/util/isStructuredTableSupportedForFile';
import kindToIcon from 'notebook/structured-tables/util/kindToIcon';
import * as TransferTableUtil from 'notebook/transfer-tables/TransferTableUtil';
import {NotePart} from 'notebook/util/NoteUtilConstants';
import {WorksheetContext} from 'notebook/worksheets/WorksheetContext';
import {MODEL_NAMES} from 'results/ResultsNamingConstants';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import ColorPicker from 'ui-library/ColorPicker';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import ColorPickerIcon from 'ui-library/icons/ColorPickerIcon';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';
import MenuContentWrapper from 'ui-library/menus/MenuContentWrapper';
import OptionContainer from 'ui-library/menus/OptionContainer';
import APIUtil from 'util/APIUtil';
import * as keyboard from 'util/Keyboard';
import {phraseToCamelCase} from 'util/NameUtil';
import {assertNever, isFunction} from 'util/Types';

const {
  BOLD,
  LINK,
  ITALIC,
  UNDERLINE,
  STRIKETHROUGH,
  SUBSCRIPT,
  SUPERSCRIPT,
  FOREGROUND_COLOR,
  BACKGROUND_COLOR,
  MONOSPACE,
} = FormattersConstants;

const {BULLET, NUMBER, CHECKBOX_UNCHECKED} = ListType;
const {HEADER_1, HEADER_2, SUBHEADER_1, PARAGRAPH, AI_CONTENT, AI_CHECKS, AI_DATA_ENTRY} = NoteConstants;

function or_(...fns: Array<(props: NoteToolbarProps) => boolean>): (props: NoteToolbarProps) => boolean {
  return (props) => _.any(Array.from(fns).map((fn) => fn(props)));
}

function and_(...fns: Array<(props: NoteToolbarProps) => boolean>): (props: NoteToolbarProps) => boolean {
  return (props) => _.all(Array.from(fns).map((fn) => fn(props)));
}

function isNotLocked(props: NoteToolbarProps): boolean {
  return !props.locked;
}

function hasSingleSelection(props: NoteToolbarProps): boolean {
  return props.hasSelection;
}

function hasAnySelection(props: NoteToolbarProps): boolean {
  return props.hasSelection || props.hasMultiSelection;
}

function hasAnyNonEmptySelection(props: NoteToolbarProps): boolean {
  return props.hasNonEmptyTextSelection || props.hasMultiSelection;
}

function isItemSelected(props: NoteToolbarProps): boolean {
  return props.itemSelected;
}

function isItemAlignable(props: NoteToolbarProps): boolean {
  return props.itemAlignable;
}

function rangeHasTextType(props: NoteToolbarProps): boolean {
  return props.rangeHasTextType;
}

function inTextType(props: NoteToolbarProps): boolean {
  return hasSingleSelection(props) && !props.itemSelected && props.rangeHasTextType;
}

function inText(props: NoteToolbarProps): boolean {
  return hasSingleSelection(props) && !props.itemSelected && props.rangeHasText;
}

function hasAnyTextSelection(props: NoteToolbarProps): boolean {
  return hasAnySelection(props) && !props.itemSelected && props.rangeHasText;
}

const isFormattable = and_(isNotLocked, hasAnyTextSelection);

function rangeHasNoSubheader1(props: NoteToolbarProps): boolean {
  return props.rangeHeaderType !== SUBHEADER_1;
}

function checkFormat(format: string, props: NoteToolbarProps): boolean | string {
  return props.formatState[format];
}

function getRequestChangeForColor(format: string, props: NoteToolbarProps): (color: string) => void {
  return (color) => {
    props.formatHandler(format, {color});
  };
}

const isDataTableFormatEnabled = and_(
  isNotLocked,
  hasSingleSelection,
  (props) => getDataTableFormat(props) != null
);

function getDataTableFormat(props: NoteToolbarProps): string | null {
  const fileType = APIUtil.apiIdToType(props.fileId);
  if (DataTableUtil.isDataTableSupportedForFileType(fileType, props.isConfig)) {
    return ModelTypeConstants.DATA_TABLE;
  }
  if (DataTableUtil.isDataTableConfigSupportedForFileType(fileType, props.isConfig)) {
    return ModelTypeConstants.DATA_TABLE_CONFIG;
  }
  return null;
}

function isEntryOrStageEntryOrTemplate({fileId}: {fileId: string}): boolean {
  return APIUtil.isEntryOrSubOrWorkflowTemplate(fileId) || APIUtil.isBaseEntry(fileId);
}

export enum DropdownFormat {
  SYMBOL = 'symbol',
}

interface DropdownItemBase {
  format?: string | ((props: NoteToolbarProps) => string);
  icon?: BenchlingIconName | ((props: NoteToolbarProps) => JSX.Element);
  text?: JSX.Element | string | (() => JSX.Element);
  plainText?: string;
  visible?: (props: NoteToolbarProps) => boolean;
  enabled?: (props: NoteToolbarProps) => boolean;
  submenu?: (props: NoteToolbarProps) => JSX.Element;
  active?: (props: NoteToolbarProps) => boolean;
  tooltip?: Array<string>;
  dataTestAttrs?: DataTestAttrs;
}

export interface ChildDropdownItem extends DropdownItemBase {
  format: string | ((props: NoteToolbarProps) => string);
  icon?: BenchlingIconName;
}

export interface DropdownItem extends DropdownItemBase {
  dropdown?: Array<Array<ChildDropdownItem>>;
  moreKey?: string;
}

export function getInsertGroupDropdownSections(): Array<Array<ChildDropdownItem>> {
  return (
    [
      [
        {
          format: 'subtemplate',
          icon: 'subtemplate',
          text: 'Sub-Template',
          plainText: 'Sub-Template',
          visible(props: NoteToolbarProps): boolean {
            return props.showInsertSubtemplate;
          },
          enabled: and_(isNotLocked, hasSingleSelection),
          dataTestAttrs: {component: 'NoteToolbar', element: 'insertSubtemplate'},
        },
      ],
      [
        {
          format: 'section',
          icon: 'list-ul',
          text(): JSX.Element {
            return (
              <span>
                <span id="insertSection-noteToolBar">Section</span>
                <span className="faded pull-right">{keyboard.ctrl} + \</span>
              </span>
            );
          },
          plainText: 'New section',
          visible(props: NoteToolbarProps): boolean {
            return props.showInsertSection;
          },
          enabled: and_(isNotLocked, hasSingleSelection),
          dataTestAttrs: {component: 'NoteToolbar', element: 'insertSection'},
        },
        {
          format: 'day',
          icon: 'format-calendar',
          text(): JSX.Element {
            return (
              <span>
                <span id="insertDay-noteToolBar">Day</span>
                <span className="faded pull-right">{keyboard.ctrl} + \</span>
              </span>
            );
          },
          plainText: 'New day',
          visible(props: NoteToolbarProps): boolean {
            return props.showInsertDay;
          },
          enabled: and_(isNotLocked, hasSingleSelection),
          dataTestAttrs: {component: 'NoteToolbar', element: 'insertDay'},
        },
        {
          format: 'table',
          icon: 'note-table',
          text: 'Table',
          plainText: 'Table',
          enabled: and_(isNotLocked, hasSingleSelection),
          submenu(props: NoteToolbarProps): JSX.Element {
            return (
              <OptionContainer key="table" isHovered={false} disableEventPropagation={true}>
                <InsertTableComponent insertTableWithDimensions={props.insertTableWithDimensions} />
              </OptionContainer>
            );
          },
          dataTestAttrs: {component: 'NoteToolbar', element: 'insertPlainTable'},
        },
        {
          format: 'well-plate',
          icon: 'well-plate',
          text: 'Well plate',
          plainText: 'Well Plate',
          enabled: and_(isNotLocked, hasSingleSelection),
          submenu({insertWellPlateWithDimensions}: NoteToolbarProps): JSX.Element {
            return (
              <MenuContentWrapper key="well-plate">
                <InsertWellPlateMenu insertWellPlateWithDimensions={insertWellPlateWithDimensions} />
              </MenuContentWrapper>
            );
          },
          dataTestAttrs: {component: 'NoteToolbar', element: 'insertWellPlate'},
        },
        {
          format: NoteConstants.NOTE_FILL_IN_TEXT_BOX,
          icon: 'text',
          text: 'Text box',
          plainText: 'Text box',
          enabled: and_(isNotLocked, hasSingleSelection),
          visible: (props: NoteToolbarProps) =>
            APIUtil.isEntryTemplate(props.fileId) ||
            APIUtil.isSubtemplate(props.fileId) ||
            (APIUtil.isDocument(props.fileId) && props.isConfig),
          dataTestAttrs: {component: 'NoteToolbar', element: 'insertNoteFillInTextBox'},
        },
        {
          format: 'attach',
          icon: 'note-attach',
          text: 'Attachment',
          plainText: 'Attachment',
          enabled: and_(isNotLocked, or_(inTextType, isItemSelected)),
          dataTestAttrs: {component: 'NoteToolbar', element: 'insertAttachment'},
        },
        {
          format: 'code',
          icon: 'note-code',
          text: 'Code block',
          plainText: 'Code block',
          enabled: and_(isNotLocked, hasSingleSelection),
          dataTestAttrs: {component: 'NoteToolbar', element: 'insertCodeBlock'},
        },
        {
          format: DropdownFormat.SYMBOL,
          icon: 'note-symbol',
          text: (
            <span>
              <span className="faded pull-right">{keyboard.ctrl} + ;</span>
              <span>Symbol</span>
            </span>
          ),
          plainText: 'Symbol',
          enabled: and_(isNotLocked, hasSingleSelection, hasAnyTextSelection),
          dataTestAttrs: {component: 'NoteToolbar', element: 'insertSymbol'},
        },
        {
          format: 'saved-search',
          icon: 'search',
          text: 'Saved search',
          plainText: 'Saved search',
          visible(props: NoteToolbarProps): boolean {
            return props.showInsertSavedSearch;
          },
          enabled: and_(isNotLocked, hasSingleSelection, hasAnyTextSelection),
          dataTestAttrs: {component: 'NoteToolbar', element: 'insertSavedSearch'},
        },
        {
          format: ModelTypeConstants.MOLECULE,
          icon: FolderItemIcon.SmallMolecule,
          text: 'Chemical structure',
          plainText: 'Chemical structure',
          enabled: and_(isNotLocked, hasSingleSelection),
          visible(): boolean {
            return BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE;
          },
          dataTestAttrs: {component: 'NoteToolbar', element: 'insertChemical'},
        },
      ],
      [
        {
          format: ModelTypeConstants.ASSAY_RUN,
          icon: ModelTypeConstants.ASSAY_RUN,
          text: MODEL_NAMES.Run,
          plainText: MODEL_NAMES.Run,
          visible: and_(isEntryOrStageEntryOrTemplate, () =>
            areAnyFeaturesEnabled([{feature: LabAutomationFeature.LAB_AUTOMATION_CREATE}])
          ),
          enabled: and_(isNotLocked, hasSingleSelection),
          dataTestAttrs: {component: 'NoteToolbar', element: 'insertRun'},
        },
      ],
      PARAMETER_TABLE_INSERT_OPTIONS,
      [
        {
          // Fallback to DATA_TABLE is specified so that propType validation doesn't fail when rendering the
          // StaticFormatToolbar. Because `!enabled`, the format value will never get used.
          format: (props: NoteToolbarProps) => getDataTableFormat(props) || ModelTypeConstants.DATA_TABLE,
          icon: 'note-table',
          text: MODEL_NAMES.Result_table,
          plainText: MODEL_NAMES.Result_table,
          enabled: isDataTableFormatEnabled,
          visible(props: NoteToolbarProps): boolean {
            return isDataTableFeatureEnabled() && !APIUtil.isBiologicalEntity(props.fileId);
          },
          dataTestAttrs: {component: 'NoteToolbar', element: 'insertResultsTable'},
        },
        {
          format: ModelTypeConstants.REGISTRATION_TABLE,
          icon: 'register',
          text: 'Registration table',
          plainText: 'Registration table',
          enabled: and_(isNotLocked, hasSingleSelection),
          visible(props: NoteToolbarProps): boolean {
            return RegistrationTableUtil.isRegistrationTableSupportedForFile(props.fileId, props.isConfig);
          },
          dataTestAttrs: {component: 'NoteToolbar', element: 'insertRegistrationTable'},
        },
        {
          format: ModelTypeConstants.REGISTRATION_TABLE_CONFIG,
          icon: 'register',
          text: 'Registration table',
          plainText: 'Registration table',
          enabled: and_(isNotLocked, hasSingleSelection),
          visible(props: NoteToolbarProps): boolean {
            return RegistrationTableUtil.isRegistrationTableConfigSupportedForFile(
              props.fileId,
              props.isConfig
            );
          },
          dataTestAttrs: {component: 'NoteToolbar', element: 'insertRegistrationTable'},
        },
        {
          format: ModelTypeConstants.TRANSFER_TABLE,
          icon: 'transfer',
          text: BenchlingConfig.ELN_ENABLE_INSERT_INVENTORY_TABLE ? 'Inventory table' : 'Transfer table',
          plainText: BenchlingConfig.ELN_ENABLE_INSERT_INVENTORY_TABLE ? 'Inventory table' : 'Transfer table',
          enabled: and_(isNotLocked, hasSingleSelection),
          visible(props: NoteToolbarProps): boolean {
            return TransferTableUtil.isTransferTableSupportedForFile(props.fileId, props.isConfig);
          },
          dataTestAttrs: {component: 'NoteToolbar', element: 'insertTransferTable'},
        },
        {
          format: ModelTypeConstants.TRANSFER_TABLE_CONFIG,
          icon: 'transfer',
          text: BenchlingConfig.ELN_ENABLE_INSERT_INVENTORY_TABLE ? 'Inventory table' : 'Transfer table',
          plainText: BenchlingConfig.ELN_ENABLE_INSERT_INVENTORY_TABLE ? 'Inventory table' : 'Transfer table',
          enabled: and_(isNotLocked, hasSingleSelection),
          visible(props: NoteToolbarProps): boolean {
            return TransferTableUtil.isTransferTableConfigSupportedForFile(props.fileId, props.isConfig);
          },
          dataTestAttrs: {component: 'NoteToolbar', element: 'insertTransferTable'},
        },
        {
          format: NoteConstants.FIXED_PLATE_EDITOR,
          icon: kindToIcon(StructuredTableKind.PLATE_CREATION),
          text: 'Plate map',
          plainText: 'Plate map',
          enabled: and_(isNotLocked, hasSingleSelection),
          visible: allowPlateMaps,
          dataTestAttrs: {component: 'NoteToolbar', element: 'insertFixedPlateEditor'},
        },
        {
          format: NoteConstants.PLATE_CREATION_TABLE,
          icon: kindToIcon(StructuredTableKind.PLATE_CREATION),
          text: 'Plate creation table',
          plainText: 'Plate creation table',
          enabled: and_(isNotLocked, hasSingleSelection),
          visible(props: NoteToolbarProps): boolean {
            return isStructuredTableSupportedForFile(StructuredTableKind.PLATE_CREATION, props.fileId);
          },
          dataTestAttrs: {component: 'NoteToolbar', element: 'insertPlateCreationTable'},
        },
        {
          format: NoteConstants.BOX_CREATION_TABLE,
          icon: kindToIcon(StructuredTableKind.BOX_CREATION),
          text: 'Box creation table',
          plainText: 'Box creation table',
          enabled: and_(isNotLocked, hasSingleSelection),
          visible(props: NoteToolbarProps): boolean {
            return isStructuredTableSupportedForFile(StructuredTableKind.BOX_CREATION, props.fileId);
          },
          dataTestAttrs: {component: 'NoteToolbar', element: 'insertBoxCreationTable'},
        },
        {
          format: NoteConstants.MIXTURE_PREP_GROUP,
          icon: FolderItemIcon.Mixture,
          text: 'Mixture prep table',
          plainText: 'Mixture prep table',
          enabled: and_(isNotLocked, hasSingleSelection),
          visible(props: NoteToolbarProps): boolean {
            return (
              isFeatureEnabled({feature: RegistryFeature.REGISTRY_MIXTURES}) &&
              !APIUtil.isBiologicalEntity(props.fileId)
            );
          },
          dataTestAttrs: {component: 'NoteToolbar', element: 'insertMixturePrepTable'},
        },
        {
          format: ModelTypeConstants.LOOKUP_TABLE,
          icon: 'search',
          text: 'Lookup table',
          plainText: 'Lookup table',
          enabled: and_(isNotLocked, hasSingleSelection),
          visible(props: NoteToolbarProps): boolean {
            return isStructuredTableSupportedForFile(StructuredTableKind.LOOKUP, props.fileId);
          },
          dataTestAttrs: {component: 'NoteToolbar', element: 'lookupTable'},
        } satisfies DropdownItem,
        {
          format: NoteConstants.NOTE_LINKED_APP_CANVAS,
          icon: 'plug',
          text: 'Canvas',
          plainText: 'Canvas',
          visible(props: NoteToolbarProps): boolean {
            // TODO (BNCH-107988): Update the visibility conditions for the app canvas plugin based on comprehesive locations list
            return (
              APIUtil.isEntry(props.fileId) ||
              APIUtil.isEntryTemplate(props.fileId) ||
              APIUtil.isSubtemplate(props.fileId) ||
              APIUtil.isDocument(props.fileId)
            );
          },
          enabled: and_(isNotLocked, hasSingleSelection, hasAnyTextSelection),
          dataTestAttrs: {component: 'NoteToolbar', element: 'insertAppCanvas'},
        } satisfies DropdownItem,
      ],
    ] satisfies Array<Array<ChildDropdownItem>>
  ).filter((value) => Boolean(value));
}

// BIOPROCESS PARAMETER TABLES
export const ParameterTableModeToInsertOptionName = {
  [ParameterStructuredTableMode.MATERIAL_INPUT_CONFIRMATION]: 'Material input table',
  [ParameterStructuredTableMode.MATERIAL_OUTPUT_CONFIRMATION]: 'Material output table',
  [ParameterStructuredTableMode.PARAMETER_CONFIRMATION]: 'Parameter setpoints table',
  [ParameterStructuredTableMode.PARAMETER_ACTUAL]: 'Parameter measurements table',
  [ParameterStructuredTableMode.EQUIPMENT_CONFIRMATION]: 'Equipment table',
};
function getParameterTableInsertOptionProps(
  parameterStructuredTableMode: ParameterStructuredTableMode
): ChildDropdownItem {
  const name = ParameterTableModeToInsertOptionName[parameterStructuredTableMode];
  return {
    format: parameterStructuredTableMode,
    text: name,
    plainText: name,
    icon: ParameterStructuredTableModeToIcon[parameterStructuredTableMode],
    enabled: and_(isNotLocked, hasSingleSelection),
    visible(props: NoteToolbarProps): boolean {
      const isProcedureStepExecution = props.deprecatedStepCreationContextId
        ? APIUtil.isProcedureStepExecutionInstance(props.deprecatedStepCreationContextId)
        : false;
      if (isProcedureStepExecution) {
        if (parameterStructuredTableMode === ParameterStructuredTableMode.MATERIAL_OUTPUT_CONFIRMATION) {
          return false;
        }
        return true;
      } else {
        return props.insertableStructuredTableTypes?.includes(parameterStructuredTableMode) || false;
      }
    },
    dataTestAttrs: {component: 'NoteToolbar', element: phraseToCamelCase(name)},
  };
}
const PARAMETER_TABLE_INSERT_OPTIONS: Array<ChildDropdownItem> = [
  getParameterTableInsertOptionProps(ParameterStructuredTableMode.MATERIAL_INPUT_CONFIRMATION),
  getParameterTableInsertOptionProps(ParameterStructuredTableMode.MATERIAL_OUTPUT_CONFIRMATION),
  getParameterTableInsertOptionProps(ParameterStructuredTableMode.PARAMETER_CONFIRMATION),
  getParameterTableInsertOptionProps(ParameterStructuredTableMode.PARAMETER_ACTUAL),
  getParameterTableInsertOptionProps(ParameterStructuredTableMode.EQUIPMENT_CONFIRMATION),
];

const INSERT_GROUP: Array<DropdownItem> = [
  {
    text: 'Insert',
    plainText: 'Insert',
    dropdown: getInsertGroupDropdownSections(),
    visible: (props: NoteToolbarProps) => !props.isProtected,
    dataTestAttrs: {component: 'NoteToolbar', element: 'insert'},
  },
];

export function getHeaderGroupDropdownSections(): Array<Array<ChildDropdownItem>> {
  return [
    [
      {
        text: <span className="header-preview is-header1">Header 1</span>,
        plainText: 'Header 1',
        format: HEADER_1,
        enabled: and_(isNotLocked, rangeHasTextType),
        active(props: NoteToolbarProps): boolean {
          return props.rangeHeaderType === HEADER_1;
        },
      },
      {
        text: <span className="header-preview is-header2">Header 2</span>,
        plainText: 'Header 2',
        format: HEADER_2,
        enabled: and_(isNotLocked, rangeHasTextType),
        active(props: NoteToolbarProps): boolean {
          return props.rangeHeaderType === HEADER_2;
        },
      },
      {
        text: <span className="header-preview is-subheader1">Subheader 1</span>,
        plainText: 'Subheader 1',
        format: SUBHEADER_1,
        enabled: and_(isNotLocked, rangeHasTextType),
        active(props: NoteToolbarProps): boolean {
          return props.rangeHeaderType === SUBHEADER_1;
        },
      },
      {
        text: <span className="header-preview is-paragraph">Paragraph</span>,
        plainText: 'Paragraph',
        format: PARAGRAPH,
        enabled: and_(isNotLocked, rangeHasTextType),
        active(props: NoteToolbarProps): boolean {
          return props.rangeHeaderType === PARAGRAPH;
        },
      },
    ],
  ];
}

const HEADER_GROUP: Array<DropdownItem> = [
  {
    icon: 'format-header',
    visible: (props: NoteToolbarProps) => !props.isProtected,
    dropdown: getHeaderGroupDropdownSections(),
    tooltip: ['Header (Ctrl + `)'],
  },
];

const FORMAT_TEXT_GROUP: Array<DropdownItem> = [
  {
    format: BOLD,
    icon: 'format-bold',
    active: checkFormat.bind(null, BOLD),
    // Disable bolding for SUBHEADER_1 because it is always bold
    enabled: and_(isFormattable, rangeHasNoSubheader1),
  },
  {
    format: ITALIC,
    icon: 'format-italic',
    active: checkFormat.bind(null, ITALIC),
    enabled: isFormattable,
  },
  {
    format: UNDERLINE,
    icon: 'format-underline',
    active: checkFormat.bind(null, UNDERLINE),
    enabled: isFormattable,
  },
  {
    format: STRIKETHROUGH,
    icon: 'format-strikethrough',
    active: checkFormat.bind(null, STRIKETHROUGH),
    enabled: isFormattable,
  },
  {
    format: LINK,
    icon: 'format-link',
    enabled: and_(isNotLocked, inText),
  },
  {
    format: MONOSPACE,
    icon: 'note-code',
    active: checkFormat.bind(null, MONOSPACE),
    enabled: and_(isFormattable, hasAnyNonEmptySelection),
  },
];

const COLOR_GROUP: Array<DropdownItem> = [
  {
    format: 'color',
    icon(props: NoteToolbarProps): JSX.Element {
      return (
        <ColorPickerIcon
          foregroundColor={
            isFormattable(props) ? (checkFormat(FOREGROUND_COLOR, props) as string) : undefined
          }
          backgroundColor={
            isFormattable(props) ? (checkFormat(BACKGROUND_COLOR, props) as string) : undefined
          }
        />
      );
    },
    enabled: isFormattable,
    dataTestAttrs: {component: 'NoteToolbar', element: 'colorPicker'},
    submenu(props: NoteToolbarProps): JSX.Element {
      return (
        <ColorPicker
          foregroundColorLink={{
            value: checkFormat(FOREGROUND_COLOR, props) as string,
            requestChange: getRequestChangeForColor(FOREGROUND_COLOR, props),
          }}
          backgroundColorLink={{
            value: checkFormat(BACKGROUND_COLOR, props) as string,
            requestChange: getRequestChangeForColor(BACKGROUND_COLOR, props),
          }}
        />
      );
    },
  },
];

const ADDL_FORMAT_TEXT_GROUP: Array<DropdownItem> = [
  {
    format: SUBSCRIPT,
    icon: 'format-subscript',
    active: checkFormat.bind(null, SUBSCRIPT),
    enabled: isFormattable,
  },
  {
    format: SUPERSCRIPT,
    icon: 'format-superscript',
    active: checkFormat.bind(null, SUPERSCRIPT),
    enabled: isFormattable,
  },
];

const TOGGLEABLE_GROUP: Array<DropdownItem> = [
  {
    format: BULLET,
    icon: 'format-bullet',
    enabled: and_(isNotLocked, rangeHasTextType),
    visible: (props: NoteToolbarProps) => !props.isProtected,
    active(props: NoteToolbarProps): boolean {
      return props.allBullets;
    },
  },
  {
    format: NUMBER,
    icon: 'format-number',
    enabled: and_(isNotLocked, rangeHasTextType),
    visible: (props: NoteToolbarProps) => !props.isProtected,
    active(props: NoteToolbarProps): boolean {
      return props.allNumbers;
    },
  },
  {
    format: CHECKBOX_UNCHECKED,
    icon: 'format-checkbox',
    enabled: and_(isNotLocked, rangeHasTextType),
    visible: (props: NoteToolbarProps) => !props.isProtected,
    active(props: NoteToolbarProps): boolean {
      return props.allCheckboxes;
    },
  },
];

const EDIT_ITEM_GROUP = (
  [
    {
      format: 'left',
      icon: 'format-left',
      active(props: NoteToolbarProps): boolean {
        return props.itemLeft;
      },
    },
    {
      format: 'center',
      icon: 'format-center',
      active(props: NoteToolbarProps): boolean {
        return props.itemCenter;
      },
    },
    {
      format: 'right',
      icon: 'format-right',
      active(props: NoteToolbarProps): boolean {
        return props.itemRight;
      },
    },
  ] satisfies Array<DropdownItem>
).map((option: DropdownItem) => {
  option.visible = and_(isNotLocked, isItemSelected, isItemAlignable);
  return option;
});

const AI_TOOLS_GROUP: Array<DropdownItem> = [
  {
    text: 'AI tools',
    plainText: 'AI tools',
    icon: 'magic-wand',
    dropdown: [
      [
        {
          format: AI_CONTENT,
          icon: 'pen',
          text: 'Write text',
          enabled: and_(isNotLocked, hasSingleSelection),
          visible: () => BenchlingConfig.ELN_ENABLE_AI_TOOLS,
          dataTestAttrs: {component: 'NoteToolbar', element: 'aiContentMenuItem'},
        },
        {
          format: AI_DATA_ENTRY,
          icon: 'plus',
          text: 'Insert data',
          enabled: (props: NoteToolbarProps) =>
            Boolean(
              isNotLocked(props) &&
                hasSingleSelection(props) &&
                props.dataEntryStructuredTableNoteParts &&
                props.dataEntryStructuredTableNoteParts?.length > 0
            ),
          visible: () => BenchlingConfig.ELN_ENABLE_AI_DATA_ENTRY,
          dataTestAttrs: {component: 'NoteToolbar', element: 'aiDataEntryMenuItem'},
        },
        {
          format: AI_CHECKS,
          icon: 'magnifying-glass',
          text: 'Check entry',
          enabled: and_(isNotLocked, hasSingleSelection),
          visible: () => BenchlingConfig.ELN_ENABLE_AI_CHECKER,
          dataTestAttrs: {component: 'NoteToolbar', element: 'aiChecksMenuItem'},
        },
      ],
    ],
    enabled: and_(isNotLocked, hasSingleSelection),
    visible: (props: NoteToolbarProps) =>
      // AI tools are currently only available for plain entries, and can be added in more places
      // in the future.
      (BenchlingConfig.ELN_ENABLE_AI_TOOLS ||
        BenchlingConfig.ELN_ENABLE_AI_CHECKER ||
        BenchlingConfig.ELN_ENABLE_AI_DATA_ENTRY) &&
      APIUtil.isEntry(props.fileId),
    dataTestAttrs: {component: 'NoteToolbar', element: 'aiToolsButton'},
  },
];

const GROUPS: Array<Array<DropdownItem>> = [
  INSERT_GROUP,
  HEADER_GROUP,
  FORMAT_TEXT_GROUP,
  COLOR_GROUP,
  ADDL_FORMAT_TEXT_GROUP,
  TOGGLEABLE_GROUP,
  [
    {
      format: 'important',
      icon: 'format-important',
      moreKey: 'basic-step',
      enabled: and_(isNotLocked, hasAnySelection),
      visible: (props: NoteToolbarProps) => !props.isProtected,
      active(props: NoteToolbarProps): boolean {
        return props.allImportant;
      },
    },
  ],
  EDIT_ITEM_GROUP,
  [
    {
      format: 'delete',
      icon: 'format-delete',
      moreKey: 'basic-step',
      enabled: and_(isNotLocked, hasAnySelection),
      visible: (props: NoteToolbarProps) => !props.isProtected,
    },
  ],
  [{format: 'selector', moreKey: 'default', visible: (props: NoteToolbarProps) => !props.isProtected}],
  AI_TOOLS_GROUP,
];

const MINIMAL_GROUPS = [
  HEADER_GROUP,
  FORMAT_TEXT_GROUP,
  COLOR_GROUP,
  ADDL_FORMAT_TEXT_GROUP,
  TOGGLEABLE_GROUP,
];

function _setDefaults(format: DropdownItem): void {
  if (format.visible == null) {
    format.visible = (props) => true;
  }
  if (format.active == null) {
    format.active = (props) => false;
  }
  if (format.enabled == null) {
    format.enabled = (props) => true;
  }
  if (format.dropdown != null) {
    format.dropdown.forEach((fmt) => {
      fmt.forEach((inner) => {
        _setDefaults(inner);
      });
    });
  }
}

GROUPS.forEach((group) => {
  group.forEach((format) => {
    _setDefaults(format);
  });
});

export interface NoteToolbarProps {
  fileId: string;
  showInsertSavedSearch: boolean;
  showInsertSubtemplate: boolean;
  showInsertDay: boolean;
  showInsertSection: boolean;
  rangeHasText: boolean;
  rangeHasTextType: boolean;
  locked: boolean;
  isProtected: boolean;
  isConfig: boolean;
  minimal: boolean;

  hasSelection: boolean;
  hasNonEmptyTextSelection: boolean;
  hasMultiSelection: boolean;
  formatState: {[format: string]: boolean};

  rangeHeaderType?: string;
  allImportant: boolean;
  allNumbers: boolean;
  allBullets: boolean;
  allCheckboxes: boolean;

  itemOnPart: boolean;
  itemLeft: boolean;
  itemRight: boolean;
  itemCenter: boolean;
  itemSelected: boolean;
  itemAlignable: boolean;

  formatHandler: (format: string, data: FormatData | undefined) => void;
  insertTableWithDimensions: () => void;
  insertWellPlateWithDimensions: (y: number, x: number) => void;
  insertHandler: () => void;
  mouseOverHandler: () => void;
  mouseOutHandler: () => void;

  printHandler: () => void;
  printOptions: Array<PRINT_OPTION>;

  undoHandler: (selectionRestoreData: {selection: SelectionRestoreData}) => void;
  redoHandler: (selectionRestoreData: {selection: SelectionRestoreData}) => void;

  // The additional table insert options defined on a document's parent doc
  insertableStructuredTableTypes?: Array<string>;
  /**
   * @deprecated
   * TODO(BNCH-117659): Rework usages to use ProcedureWorksheetContext.PMEIs instead
   */
  deprecatedStepCreationContextId?: string | null;

  dataEntryStructuredTableNoteParts?: Array<{
    notePart: NotePart;
    index: number;
    table: StructuredTable<unknown, unknown>;
  }>;
}

class InnerNoteToolbar extends React.PureComponent<NoteToolbarProps> {
  static propTypes = {
    fileId: PropTypes.string.isRequired,
    showInsertDay: PropTypes.bool.isRequired,
    rangeHasText: PropTypes.bool.isRequired,
    rangeHasTextType: PropTypes.bool.isRequired,
    locked: PropTypes.bool.isRequired,
    isConfig: PropTypes.bool.isRequired,

    hasSelection: PropTypes.bool.isRequired,
    hasNonEmptyTextSelection: PropTypes.bool.isRequired,
    hasMultiSelection: PropTypes.bool.isRequired,
    formatState: FormatState.propType.isRequired,

    rangeHeaderType: PropTypes.string,
    allImportant: PropTypes.bool.isRequired,
    allNumbers: PropTypes.bool.isRequired,
    allBullets: PropTypes.bool.isRequired,
    allCheckboxes: PropTypes.bool.isRequired,

    itemOnPart: PropTypes.bool.isRequired,
    itemLeft: PropTypes.bool.isRequired,
    itemRight: PropTypes.bool.isRequired,
    itemCenter: PropTypes.bool.isRequired,
    itemSelected: PropTypes.bool.isRequired,
    itemAlignable: PropTypes.bool.isRequired,

    formatHandler: PropTypes.func.isRequired,
    insertTableWithDimensions: PropTypes.func.isRequired,
    insertWellPlateWithDimensions: PropTypes.func.isRequired,
    insertHandler: PropTypes.func.isRequired,
    mouseOverHandler: PropTypes.func.isRequired,
    mouseOutHandler: PropTypes.func.isRequired,

    printHandler: PropTypes.func.isRequired,
    printOptions: PropTypes.arrayOf(PropTypes.string).isRequired,

    insertableStructuredTableTypes: PropTypes.arrayOf(PropTypes.string),
    deprecatedStepCreationContextId: PropTypes.string,
  };

  _resolveProp = <T,>(prop: T | ((props: NoteToolbarProps) => T), defaultReturn?: T): T =>
    prop != null && isFunction(prop) ? prop(this.props) : (defaultReturn ?? prop);

  _mapChildOption(option: ChildDropdownItem): ToolbarFormatOption {
    return {
      active: this._resolveProp(option.active, undefined),
      enabled: this._resolveProp<boolean>(option.enabled ?? true),
      format: this._resolveProp(option.format),
      icon: option.icon,
      submenu: this._resolveProp(option.submenu, undefined),
      text: this._resolveProp(option.text),
      tooltip: option.tooltip,
      visible: this._resolveProp<boolean>(option.visible ?? true),
      dataTestAttrs: option.dataTestAttrs,
    };
  }

  _mapDropdownOption(option: DropdownItem): ToolbarFormatOptionWithDropdown {
    return {
      active: this._resolveProp(option.active, undefined),
      dropdown:
        option.dropdown != null
          ? option.dropdown.map((section) =>
              section.map((opt): ToolbarFormatOption => this._mapChildOption(opt))
            )
          : undefined,
      enabled: this._resolveProp<boolean>(option.enabled ?? true),
      format: this._resolveProp(option.format),
      icon: typeof option.icon === 'string' ? option.icon : this._resolveProp(option.icon, undefined),
      moreKey: option.moreKey,
      submenu: this._resolveProp(option.submenu, undefined),
      text: this._resolveProp(option.text),
      tooltip: option.tooltip,
      visible: this._resolveProp<boolean>(option.visible ?? true),
      dataTestAttrs: option.dataTestAttrs,
    };
  }

  /**
   * Return groups to be rendered in NoteToolbar.
   */
  getGroups(): Array<Array<ToolbarFormatOptionWithDropdown>> {
    // Only return groups for unlocked templates and entries.
    if (this.props.locked) {
      return [];
    }
    const groups = this.props.minimal ? MINIMAL_GROUPS : GROUPS;
    return groups.map((group) => group.map((option) => this._mapDropdownOption(option)));
  }

  _getMappedPrintOptions(printOptions: Array<PRINT_OPTION>): Array<{type: PRINT_OPTION; enabled: boolean}> {
    // eslint-disable-next-line array-callback-return
    return printOptions.map((printOption) => {
      switch (printOption) {
        case PRINT_OPTION.NOTE:
          return {type: PRINT_OPTION.NOTE, enabled: true};
        case PRINT_OPTION.ENTRY:
          return {type: PRINT_OPTION.ENTRY, enabled: true};
        case PRINT_OPTION.TEMPLATE:
          return {type: PRINT_OPTION.TEMPLATE, enabled: true};
        case PRINT_OPTION.SELECTION:
          return {
            type: PRINT_OPTION.SELECTION,
            enabled: isItemSelected(this.props) || hasAnyNonEmptySelection(this.props),
          };
        default:
          assert(false, `Unexpected print option: ${assertNever(printOption)}`);
      }
    });
  }

  render(): JSX.Element {
    return (
      <StaticFormatToolbar
        groups={this.getGroups()}
        isLocked={this.props.locked}
        formatHandler={this.props.formatHandler}
        insertHandler={this.props.insertHandler}
        mouseOverHandler={this.props.mouseOverHandler}
        mouseOutHandler={this.props.mouseOutHandler}
        printOptions={this._getMappedPrintOptions(this.props.printOptions)}
        printHandler={this.props.printHandler}
        undoHandler={this.props.undoHandler}
        redoHandler={this.props.redoHandler}
      />
    );
  }
}

export default function NoteToolbar(
  props: Omit<NoteToolbarProps, 'deprecatedStepCreationContextId'>
): JSX.Element {
  const {deprecatedStepCreationContextId} = WorksheetContext.useContext();
  return <InnerNoteToolbar {...props} deprecatedStepCreationContextId={deprecatedStepCreationContextId} />;
}
